// Copilot message bubble component (SPEC 001 T142)
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CopilotMessage, CopilotMessageRole } from "../../interfaces/Copilot";

interface CopilotMessageBubbleProps {
  message: CopilotMessage;
  onEdit?: (messageId: string, content: string) => void;
  editDisabled?: boolean;
}

function roleLabel(role: CopilotMessageRole): string {
  switch (role) {
    case CopilotMessageRole.User:
      return "Você";
    case CopilotMessageRole.Assistant:
      return "NINA";
    case CopilotMessageRole.Tool:
      return "Ferramenta";
    case CopilotMessageRole.System:
      return "Sistema";
    default:
      return "Desconhecido";
  }
}

function roleBubbleClass(role: CopilotMessageRole): string {
  switch (role) {
    case CopilotMessageRole.User:
      return "ml-auto bg-dv-blue-600 text-white";
    case CopilotMessageRole.Assistant:
      return "mr-auto bg-gray-100 text-gray-900 dark:bg-navy-700 dark:text-white";
    case CopilotMessageRole.Tool:
      return "mr-auto bg-yellow-50 text-yellow-900 border border-yellow-200 dark:bg-navy-800 dark:text-yellow-300";
    case CopilotMessageRole.System:
      return "mx-auto bg-gray-200 text-gray-700 text-xs italic dark:bg-navy-800 dark:text-gray-300";
    default:
      return "mr-auto bg-gray-100 text-gray-900 dark:bg-navy-700 dark:text-white";
  }
}

const markdownComponents = {
  p: ({ node, ...props }: any) => (
    <p className="mb-2 last:mb-0 leading-relaxed" {...props} />
  ),
  a: ({ node, children, ...props }: any) => (
    <a
      className="text-dv-blue-600 dark:text-dv-cyan-300 underline hover:no-underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />
  ),
  li: ({ node, ...props }: any) => <li className="leading-relaxed" {...props} />,
  h1: ({ node, children, ...props }: any) => (
    <h1 className="text-base font-semibold mt-1 mb-1.5" {...props}>
      {children}
    </h1>
  ),
  h2: ({ node, children, ...props }: any) => (
    <h2 className="text-sm font-semibold mt-1 mb-1.5" {...props}>
      {children}
    </h2>
  ),
  h3: ({ node, children, ...props }: any) => (
    <h3 className="text-sm font-semibold mt-1 mb-1" {...props}>
      {children}
    </h3>
  ),
  code: ({ node, inline, className, children, ...props }: any) =>
    inline ? (
      <code
        className="px-1 py-0.5 rounded bg-black/10 dark:bg-white/10 font-mono text-[0.85em]"
        {...props}
      >
        {children}
      </code>
    ) : (
      <code
        className="block p-2 my-1.5 rounded bg-black/10 dark:bg-white/10 font-mono text-[0.8em] overflow-x-auto whitespace-pre"
        {...props}
      >
        {children}
      </code>
    ),
  pre: ({ node, children, ...props }: any) => (
    <pre className="my-1.5 overflow-x-auto" {...props}>
      {children}
    </pre>
  ),
  blockquote: ({ node, ...props }: any) => (
    <blockquote
      className="border-l-2 border-gray-400 dark:border-navy-400 pl-2 my-1.5 italic opacity-90"
      {...props}
    />
  ),
  table: ({ node, ...props }: any) => (
    <div className="overflow-x-auto my-1.5">
      <table className="min-w-full text-xs border-collapse" {...props} />
    </div>
  ),
  th: ({ node, ...props }: any) => (
    <th
      className="border border-gray-300 dark:border-navy-500 px-2 py-1 text-left font-semibold bg-black/5 dark:bg-white/5"
      {...props}
    />
  ),
  td: ({ node, ...props }: any) => (
    <td
      className="border border-gray-300 dark:border-navy-500 px-2 py-1 align-top"
      {...props}
    />
  ),
  hr: ({ node, ...props }: any) => (
    <hr className="my-2 border-gray-300 dark:border-navy-500" {...props} />
  ),
  strong: ({ node, ...props }: any) => (
    <strong className="font-semibold" {...props} />
  ),
};

const CopilotMessageBubble: React.FC<CopilotMessageBubbleProps> = ({
  message,
  onEdit,
  editDisabled,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content ?? "");

  if (!message.content) return null;

  const renderAsMarkdown = message.role === CopilotMessageRole.Assistant;
  const isUser = message.role === CopilotMessageRole.User;
  const canEdit = isUser && !!onEdit;

  const submitEdit = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    setEditing(false);
    if (trimmed !== message.content) onEdit?.(message.id, trimmed);
  };

  if (editing) {
    return (
      <div className="flex w-full mb-2 flex-col items-end">
        <textarea
          className="w-[80%] resize-none rounded-lg border border-dv-blue-400 bg-white dark:bg-navy-800 text-sm text-gray-900 dark:text-white px-2 py-1 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/30"
          rows={2}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submitEdit();
            }
            if (e.key === "Escape") {
              setDraft(message.content ?? "");
              setEditing(false);
            }
          }}
        />
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => {
              setDraft(message.content ?? "");
              setEditing(false);
            }}
            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Cancelar
          </button>
          <button
            onClick={submitEdit}
            className="text-xs font-medium text-dv-blue-600 hover:text-dv-blue-700"
          >
            Salvar e reenviar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full mb-2 group">
      <div
        className={`max-w-[80%] rounded-xl px-3 py-2 text-sm break-words ${
          renderAsMarkdown ? "" : "whitespace-pre-wrap"
        } ${roleBubbleClass(message.role)}`}
      >
        {message.role === CopilotMessageRole.Assistant ? (
          <p className="mb-1 flex items-center gap-1.5 text-xs font-bold">
            <span
              className="dv-animated-gradient h-2 w-2 rounded-full bg-[length:200%_200%]"
              aria-hidden="true"
            />
            <span className="dv-gradient-text">NINA</span>
          </p>
        ) : (
          message.role !== CopilotMessageRole.User && (
            <p className="text-xs font-semibold mb-1 opacity-60">
              {roleLabel(message.role)}
            </p>
          )
        )}
        {renderAsMarkdown ? (
          <div className="copilot-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <p>{message.content}</p>
        )}
      </div>
      {canEdit && (
        <button
          onClick={() => {
            setDraft(message.content ?? "");
            setEditing(true);
          }}
          disabled={editDisabled}
          title="Editar mensagem"
          aria-label="Editar mensagem"
          className="self-center ml-1 text-xs text-gray-400 hover:text-dv-blue-600 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
        >
          ✎
        </button>
      )}
    </div>
  );
};

export default CopilotMessageBubble;
